import type { HTMLAttributes } from "react";
import { SearchLg } from "@untitledui/icons";
import { cx } from "@/utils/cx";

interface IllustrationProps extends HTMLAttributes<HTMLDivElement> {
    size?: "sm" | "md" | "lg";
    svgClassName?: string;
    childrenClassName?: string;
}

export const SearchIllustration = ({ size = "lg", ...otherProps }: IllustrationProps) => {
    const Pattern = sizes[size];

    return <Pattern { ...otherProps } />;
};

export const sm = ({
                       className,
                       svgClassName,
                       childrenClassName,
                       children = <SearchLg className="size-6"/>,
                       ...otherProps
                   }: Omit<IllustrationProps, "size">) => {
    return (
        <div { ...otherProps } className={ cx("relative h-31.5 w-38", className) }>
            <svg viewBox="0 0 152 126" fill="none"
                 className={ cx("size-full stroke-inherit text-inherit", svgClassName) }>
                {/* Center icon background with shadow */ }
                <g filter="url(#search-shadow-sm)">
                    <rect x="56" y="43" width="40" height="40" rx="8"
                          className="fill-utility-gray-50 stroke-border-secondary"/>
                </g>

                <defs>
                    <filter
                        id="search-shadow-sm"
                        x="42"
                        y="32"
                        width="68"
                        height="68"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                       result="hardAlpha"/>
                        <feOffset dy="3"/>
                        <feGaussianBlur stdDeviation="7"/>
                        <feColorMatrix type="matrix"
                                       values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.08 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                    </filter>
                </defs>
            </svg>

            { children && (
                <span
                    className={ cx(
                        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex size-10 items-center justify-center rounded-lg bg-utility-gray-50 border border-border-secondary text-fg-quinary",
                        childrenClassName,
                    ) }
                >
                    { children }
                </span>
            ) }
        </div>
    );
};

export const md = ({
                       className,
                       svgClassName,
                       childrenClassName,
                       children = <SearchLg className="size-7"/>,
                       ...otherProps
                   }: Omit<IllustrationProps, "size">) => {
    return (
        <div { ...otherProps } className={ cx("relative h-37 w-43", className) }>
            <svg viewBox="0 0 172 148" fill="none"
                 className={ cx("size-full stroke-inherit text-inherit", svgClassName) }>
                {/* Center icon background with shadow */ }
                <g filter="url(#search-shadow-md)">
                    <rect x="64" y="52" width="44" height="44" rx="10"
                          className="fill-utility-gray-50 stroke-border-secondary"/>
                </g>

                <defs>
                    <filter
                        id="search-shadow-md"
                        x="48"
                        y="40"
                        width="76"
                        height="76"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                       result="hardAlpha"/>
                        <feOffset dy="4"/>
                        <feGaussianBlur stdDeviation="8"/>
                        <feColorMatrix type="matrix"
                                       values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.08 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                    </filter>
                </defs>
            </svg>

            { children && (
                <span
                    className={ cx(
                        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex size-11 items-center justify-center rounded-lg bg-utility-gray-50 border border-border-secondary text-fg-quinary",
                        childrenClassName,
                    ) }
                >
                    { children }
                </span>
            ) }
        </div>
    );
};

export const lg = ({
                       className,
                       svgClassName,
                       childrenClassName,
                       children = <SearchLg className="size-7"/>,
                       ...otherProps
                   }: Omit<IllustrationProps, "size">) => {
    return (
        <div { ...otherProps } className={ cx("relative h-45.25 w-55", className) }>
            <svg viewBox="0 0 220 181" fill="none"
                 className={ cx("size-full stroke-inherit text-inherit", svgClassName) }>
                {/* Center icon background with shadow */ }
                <g filter="url(#search-shadow-lg)">
                    <rect x="85" y="65" width="50" height="50" rx="12"
                          className="fill-utility-gray-50 stroke-border-secondary"/>
                </g>

                <defs>
                    <filter
                        id="search-shadow-lg"
                        x="65"
                        y="50"
                        width="90"
                        height="90"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                       result="hardAlpha"/>
                        <feOffset dy="5"/>
                        <feGaussianBlur stdDeviation="10"/>
                        <feColorMatrix type="matrix"
                                       values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.08 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                    </filter>
                </defs>
            </svg>

            { children && (
                <span
                    className={ cx(
                        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex size-12 items-center justify-center rounded-xl bg-utility-gray-50 border border-border-secondary text-fg-quinary",
                        childrenClassName,
                    ) }
                >
                    { children }
                </span>
            ) }
        </div>
    );
};

const sizes = {
    sm,
    md,
    lg,
};
